import { moment } from "@msuite/utils/date";
import type { CSSProperties } from "react";
import { useTranslation } from "../localization";
import { cn } from "../utils";
import { useCalendarContext } from "./context";
import {
	CALENDAR_DAY_STEP_FACTOR,
	type CalendarItemTimed,
	type CalendarItem as CalendarItemType,
	calendarItem,
} from "./shared";

/** Props Interface */
interface CalendarItemProps<T extends object = object> {
	item: CalendarItemType<T>;
	date: string;
}

export function CalendarItem<T extends object>({ item, date }: CalendarItemProps<T>) {
	/** Hooks */
	const { t } = useTranslation();

	/** Context */
	const { getItemColor, renderItemContent, onItemClick } = useCalendarContext();

	/** Classes */
	const calendarItemClasses = calendarItem({
		color: getItemColor?.(item) ?? "emerald",
		type: item.durationType,
	});

	/** Functions */
	function calculateStyles(): CSSProperties {
		if (item.durationType === "all-day") {
			return {
				height: "max-content",
			};
		} else {
			const rows = calculateRows(item as CalendarItemTimed<T>, date);
			return {
				gridRow: `${rows.rowStart + 1} / span ${rows.rowSpan}`,
			};
		}
	}

	function getTimeLabel(): string {
		if (item.durationType === "all-day") {
			return t("dates.all_day");
		} else {
			return `${moment(item.startTime).getTime("HH:mm")} - ${moment(item.endTime).getTime("HH:mm")}`;
		}
	}

	/** Render */
	return (
		<div
			className={cn(calendarItemClasses, "**:text-white!")}
			style={calculateStyles()}
			onClick={() => {
				onItemClick?.(item);
			}}
		>
			<p className="text-xs text-white!">{getTimeLabel()}</p>
			{renderItemContent?.(item)}
		</div>
	);
}

function calculateRows<T extends object = object>(
	item: CalendarItemTimed<T>,
	date: string,
): {
	rowStart: number;
	rowSpan: number;
} {
	/**
	 * Basic setup for calculating the row start and span.
	 * The step size is determined by the CALENDAR_DAY_STEP_FACTOR,
	 * which defines how many steps per hour (e.g., 4 for 15-minute intervals).
	 */
	const stepSize = 60 / CALENDAR_DAY_STEP_FACTOR;

	const startDateTime = item.firstDayIsHalfDay
		? new Date(new Date(item.startTime).setHours(12, 0, 0, 0))
		: new Date(item.startTime);
	const endDateTime = item.lastDayIsHalfDay
		? new Date(new Date(item.endTime).setHours(12, 0, 0, 0))
		: new Date(item.endTime);

	const dayStart = new Date(`${date}T00:00:00`);
	const dayEnd = new Date(`${date}T23:59:59`);

	/**
	 * Clamp start and end times to the boundaries of the day.
	 * This ensures that if the event starts before the day or ends after the day,
	 */
	const clampedStart = startDateTime < dayStart ? dayStart : startDateTime;
	const clampedEnd = endDateTime > dayEnd ? dayEnd : endDateTime;

	/**
	 * Calculate the row start and span based on the clamped times.
	 * The start time is converted to the row index based on the hour and minute.
	 */
	const startHour = clampedStart.getHours();
	const startMinutes = clampedStart.getMinutes();
	const roundedStartMinutes = Math.round(startMinutes / stepSize) * stepSize;

	/**
	 * Calculate the row start index.
	 * If the start time is before the day start, it starts at row 0.
	 */
	const rowStart =
		startDateTime < dayStart
			? 0
			: startHour * CALENDAR_DAY_STEP_FACTOR + roundedStartMinutes / stepSize;

	/**
	 * Calculate the row span based on the duration of the event.
	 * The duration is calculated in milliseconds and then converted to minutes.
	 */
	const durationMs = clampedEnd.getTime() - clampedStart.getTime();
	const minutes = durationMs / 1000 / 60;
	const rowSpan = Math.ceil(minutes / stepSize);

	return { rowStart, rowSpan };
}
